import { z } from 'zod';
import { ActivitySourceSchema } from '../activity/activities.schemas';
import { ActorSchema } from '../common/actor.schema';
import { ValuationReportKindSchema } from '../valuation/valuation.schemas';

// Emitted when a reviewer RELEASES a valuation request (IMM-475.2, renamed from
// `valuation.request-completed`): the request moved AWAITING_RELEASE → RELEASED,
// the property's `property_valuation` record was written from the held — possibly
// adjusted — figures, and the document report was filed. The communications module
// subscribes to it to deliver the result to the agent (the „Bewertung fertig"
// e-mail). The prices ride the payload so the subscriber never re-reads the record;
// ids are bare cross-module references (ADR 0008). `valuationDate` is the effective
// valuation date (defaulted to the submit day when the producer left it blank).
//
// The rename is why `communications` subscribes to BOTH this and the old
// `valuation.request-completed` for one release: an outbox row written seconds
// before the deploy would otherwise never send its e-mail. A follow-up drops the
// old registration.
export const ValuationRequestReleasedPayloadSchema = z.object({
  valuationRequestId: z.uuid(),
  dealId: z.uuid(),
  propertyId: z.uuid(),
  workspaceId: z.uuid(),
  organizationId: z.uuid(),
  priceFrom: z.number(),
  priceTo: z.number(),
  recommendedStartPrice: z.number().nullable(),
  valuationDate: z.iso.date(),
  // The full held report set — 1–2 entries, at most one per kind. Replaces the
  // single `reportMediaFileId`: a valuation may carry HTML, a document, or both.
  reports: z.array(
    z.object({
      kind: ValuationReportKindSchema,
      mediaFileId: z.uuid(),
    }),
  ),
  releasedBy: ActorSchema,
  releasedAt: z.iso.datetime(),
});

export type ValuationRequestReleasedPayload = z.infer<typeof ValuationRequestReleasedPayloadSchema>;

// The envelope every IMM-475.5 lifecycle event carries. One transition = one
// event = one activity, so the payload holds exactly what the activity projection
// needs: the request and the records it hangs off, the tenant scope, WHO moved it
// and WHEN. Unlike the legacy `completed` payload — which carried a bare
// `completedByAdminId` — the actor rides the canonical `Actor` envelope, so the
// projection stamps `actorType` without inferring it from the id shape.
//
// `occurredAt` is load-bearing beyond display: the projection folds it into the
// deterministic `content_id`, so a valuation that goes back for rework twice
// yields two entries rather than collapsing onto the first.
const ValuationLifecycleEnvelope = {
  valuationRequestId: z.uuid(),
  dealId: z.uuid(),
  propertyId: z.uuid(),
  workspaceId: z.uuid(),
  organizationId: z.uuid(),
  actor: ActorSchema,
  // Which surface performed the transition (IMM-940, mirroring IMM-601.5's
  // `deal.phase-changed`): APP, or CONNECTED_CLIENT for a workspace-MCP tool
  // call. Travels on the payload so the projected timeline entry can render the
  // „Über einen verbundenen Client" marker. Optional with a default so an event
  // already sitting in the outbox across the deploy still parses — absent means
  // APP. Only `submitted` is reachable over MCP today.
  source: ActivitySourceSchema.default('APP'),
  occurredAt: z.iso.datetime(),
};

// DRAFT → REQUESTED: the agent asked for a valuation (IMM-475.5).
export const ValuationRequestSubmittedPayloadSchema = z.object({ ...ValuationLifecycleEnvelope });
export type ValuationRequestSubmittedPayload = z.infer<typeof ValuationRequestSubmittedPayloadSchema>;

// REQUESTED → DRAFT: the agent pulled the request back while it was untaken.
export const ValuationRequestWithdrawnPayloadSchema = z.object({ ...ValuationLifecycleEnvelope });
export type ValuationRequestWithdrawnPayload = z.infer<typeof ValuationRequestWithdrawnPayloadSchema>;

// REQUESTED → IN_PROGRESS: an operator claimed the request. Always an `ADMIN`
// actor — the claim is only reachable from the admin tier.
export const ValuationRequestTakenOnPayloadSchema = z.object({ ...ValuationLifecycleEnvelope });
export type ValuationRequestTakenOnPayload = z.infer<typeof ValuationRequestTakenOnPayloadSchema>;

// IN_PROGRESS → AWAITING_RELEASE: the produced result was handed to the reviewer.
export const ValuationRequestSubmittedForApprovalPayloadSchema = z.object({ ...ValuationLifecycleEnvelope });
export type ValuationRequestSubmittedForApprovalPayload = z.infer<
  typeof ValuationRequestSubmittedForApprovalPayloadSchema
>;

// AWAITING_RELEASE → IN_PROGRESS: the reviewer sent it back. `reason` rides the
// payload because the timeline entry names it — „… zurückgegeben: {Grund}".
export const ValuationRequestReturnedPayloadSchema = z.object({
  ...ValuationLifecycleEnvelope,
  reason: z.string(),
});
export type ValuationRequestReturnedPayload = z.infer<typeof ValuationRequestReturnedPayloadSchema>;

// The reviewer replaced the held figures while the valuation waited for Freigabe.
// BOTH price pairs ride the payload: an adjusted number that reaches the
// Eigentümer with no record of what it replaced is exactly what the log exists to
// prevent, and the subscriber must never re-read the request to find out.
export const ValuationResultAdjustedPayloadSchema = z.object({
  ...ValuationLifecycleEnvelope,
  previousPriceFrom: z.number(),
  previousPriceTo: z.number(),
  priceFrom: z.number(),
  priceTo: z.number(),
});
export type ValuationResultAdjustedPayload = z.infer<typeof ValuationResultAdjustedPayloadSchema>;

/**
 * The pre-IMM-475.2 completion payload, retained ONLY so the transitional
 * `valuation.request-completed` subscription can still parse outbox rows written
 * before the approval gate deployed. Nothing publishes it any more; delete it with
 * the transitional subscriber once the outbox has drained.
 *
 * @deprecated Superseded by {@link ValuationRequestReleasedPayloadSchema}.
 */
export const ValuationRequestCompletedPayloadSchema = z.object({
  valuationRequestId: z.uuid(),
  dealId: z.uuid(),
  propertyId: z.uuid(),
  workspaceId: z.uuid(),
  organizationId: z.uuid(),
  priceFrom: z.number(),
  priceTo: z.number(),
  recommendedStartPrice: z.number().nullable(),
  valuationDate: z.iso.date(),
  reportMediaFileId: z.uuid(),
  completedByAdminId: z.uuid(),
  completedAt: z.iso.datetime(),
});

export type ValuationRequestCompletedPayload = z.infer<typeof ValuationRequestCompletedPayloadSchema>;

// Emitted when an agent's UPLOADED document attachment on a valuation request is
// confirmed (IMM-447.3): the bytes are verified and the request row exists. The
// property module subscribes and files a copy into the Objekt's documents as
// UNCATEGORIZED, so the upload enriches the Objekt as a by-product of preparing
// the request. Only uploads publish — a REFERENCED attachment is already on the
// property. The verified file metadata rides the payload so the subscriber never
// re-reads it; ids are bare cross-module references (ADR 0008). `mediaFileId` is
// the REQUEST's file: the subscriber copies its bytes into a property-owned one,
// so the two copies have independent lifecycles.
export const ValuationRequestDocumentUploadedPayloadSchema = z.object({
  valuationRequestDocumentId: z.uuid(),
  valuationRequestId: z.uuid(),
  propertyId: z.uuid(),
  workspaceId: z.uuid(),
  organizationId: z.uuid(),
  mediaFileId: z.uuid(),
  contentType: z.string(),
  sizeBytes: z.number().int().nonnegative(),
  originalFilename: z.string(),
  displayName: z.string(),
  uploadedByMemberId: z.uuid(),
  uploadedAt: z.iso.datetime(),
});

export type ValuationRequestDocumentUploadedPayload = z.infer<typeof ValuationRequestDocumentUploadedPayloadSchema>;

// Emitted when an agent's UPLOADED image attachment on a valuation request is
// confirmed (IMM-447.4) — the picture counterpart of the document event above.
// The property module subscribes and files a copy into the Objekt's Bilder
// gallery as an ordinary PHOTO, never the cover image, so the upload enriches the
// Objekt as a by-product of preparing the request. Only uploads publish; the
// image set has no REFERENCED path. Same payload shape as the document event
// with the attachment id renamed, and `mediaFileId` is likewise the REQUEST's
// file — the subscriber copies its bytes into a property-owned one.
export const ValuationRequestImageUploadedPayloadSchema = z.object({
  valuationRequestImageId: z.uuid(),
  valuationRequestId: z.uuid(),
  propertyId: z.uuid(),
  workspaceId: z.uuid(),
  organizationId: z.uuid(),
  mediaFileId: z.uuid(),
  contentType: z.string(),
  sizeBytes: z.number().int().nonnegative(),
  originalFilename: z.string(),
  displayName: z.string(),
  uploadedByMemberId: z.uuid(),
  // The uploader, for the audit row (IMM-1095). Optional so a row already in
  // the outbox across the deploy still parses; the mapper falls back to
  // `uploadedByMemberId`.
  actor: ActorSchema.optional(),
  uploadedAt: z.iso.datetime(),
});

export type ValuationRequestImageUploadedPayload = z.infer<typeof ValuationRequestImageUploadedPayloadSchema>;
